import { price, qty } from '../format'
import type { Ladder, LadderRow } from '../ladder'

/**
 * Telefon için emir defteri — tek sütun, dikey.
 *
 *     4.295,8    10     ← satışlar, EN KÖTÜ ÜSTTE
 *     4.295,6    36
 *   ── makas ──
 *     4.125,4    24     ← alışlar, EN İYİ ÜSTTE
 *
 * Masaüstündeki aynalı `DepthLadder` altı sütun (K.Miktar · Miktar · Fiyat ×2)
 * ve telefon genişliğine sığmıyor. Buradaki düzen borsaların mobil standardı:
 * iki taraf sıralanınca en iyi alış ile en iyi satış ortada buluşuyor, makas
 * göz kaydırmadan okunuyor.
 *
 * **Dokunmak fiyatı emir formuna yazar.** Masaüstünde fiyata tıklamak Hızlı
 * Al/Sat balonunu açıyor; mobilde o eylem BİLEREK yok — tek dokunuş iki farklı
 * şey yapamaz ve sık olan iş fiyat girmek. Süpürme mobilde henüz açılmadı.
 *
 * Satır zeminindeki çubuk kümülatif derinlik (`ladder.scale` ortak ölçek):
 * hangi tarafın daha kalın olduğu sayı okumadan görünüyor.
 */

interface Props {
  ladder: Ladder
  hasDepth: boolean
  /** Taraf başına satır sayısı. */
  levels?: number
  onPrice: (price: number) => void
}

export function CompactLadder({ ladder, hasDepth, levels = 5, onPrice }: Props) {
  if (!hasDepth) {
    return <div className="cl cl-bos">Derinlik bekleniyor…</div>
  }

  // Satışlar ters çevriliyor: dizide en iyi (en ucuz) başta, ekranda en altta
  // olmalı ki makasın iki yakası yan yana gelsin.
  const asks = ladder.asks.slice(0, levels).reverse()
  const bids = ladder.bids.slice(0, levels)

  const enIyiAlis = ladder.bids[0]?.price
  const enIyiSatis = ladder.asks[0]?.price
  const makas = enIyiAlis != null && enIyiSatis != null ? enIyiSatis - enIyiAlis : null

  return (
    <div className="cl">
      <div className="cl-head">
        <span>Fiyat</span>
        <span>Lot</span>
      </div>

      <div className="cl-rows">
        {asks.map((row) => (
          <Satir key={`a${row.price}`} row={row} taraf="ask" scale={ladder.scale} onPrice={onPrice} />
        ))}

        <div className="cl-makas">
          {makas != null ? `makas ${price(makas)}` : '—'}
        </div>

        {bids.map((row) => (
          <Satir key={`b${row.price}`} row={row} taraf="bid" scale={ladder.scale} onPrice={onPrice} />
        ))}
      </div>
    </div>
  )
}

function Satir({
  row,
  taraf,
  scale,
  onPrice,
}: {
  row: LadderRow
  taraf: 'bid' | 'ask'
  scale: number
  onPrice: (price: number) => void
}) {
  const oran = Math.min(100, (row.cumulative / scale) * 100)
  const bizim = row.mine > 0
  const yon = taraf === 'ask' ? 'Satış' : 'Alış'

  return (
    <button
      type="button"
      className={`cl-row ${taraf}${bizim ? ' bizim' : ''}`}
      onClick={() => onPrice(row.price)}
      aria-label={`${yon} ${price(row.price)} — fiyatı emir formuna yaz`}
    >
      <span className="cl-bar" style={{ width: `${oran}%` }} aria-hidden="true" />
      <span className="cl-fiyat num">{price(row.price)}</span>
      {/* Sentetik satır = piyasada o fiyatta seviye yok, satır yalnız bizim
          emrimiz için açıldı; gösterilecek piyasa miktarı da yok. */}
      <span className="cl-lot num">{row.synthetic ? '–' : qty(row.quantity)}</span>
      {bizim && (
        <span className="cl-benim" title={`Kendi emrim: ${qty(row.mine)} lot`} aria-hidden="true" />
      )}
    </button>
  )
}
